import { useState, useEffect, useRef, Fragment } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import SafetyShell from "../safety/SafetyShell";
import {
  Ship,
  Search,
  Download,
  Trash2,
  Eye,
  RefreshCw,
  Settings2,
  Link2,
  Link2Off,
  FileUp,
  CheckCircle2,
  Mail,
  FileText,
  Files,
  ChevronRight,
  ChevronDown,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const EMPTY_CONFIG = {
  imap_server: "",
  imap_port: 993,
  imap_use_ssl: true,
  email: "",
  password: "",
  sender_filter: "noreply@icegate.gov.in",
  folder: "INBOX",
  auto_fetch_enabled: true,
};

function ShippingBills({ user, onLogout }) {
  const [permissions, setPermissions] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | matched | unmatched
  const [consigneeFilter, setConsigneeFilter] = useState("");
  const [consigneeOpts, setConsigneeOpts] = useState([]);
  const [fetching, setFetching] = useState(false);

  const [cfgStatus, setCfgStatus] = useState(null);
  const [cfgDialog, setCfgDialog] = useState(false);
  const [cfg, setCfg] = useState(EMPTY_CONFIG);
  const [savingCfg, setSavingCfg] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [previewDialog, setPreviewDialog] = useState({ open: false, row: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [deleting, setDeleting] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [reparsingAll, setReparsingAll] = useState(false);
  const [view, setView] = useState("bills"); // bills | docs
  const [documents, setDocuments] = useState([]);
  const [docLabels, setDocLabels] = useState({});
  const [docTypeFilter, setDocTypeFilter] = useState("all");
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [expandedBill, setExpandedBill] = useState(null);
  const [billDocs, setBillDocs] = useState({});
  const uploadRef = useRef(null);

  const has = (p) => permissions.includes(p);
  const canView = has("packing_list.view");
  const canDownload = has("packing_list.download");
  const canDelete = has("packing_list.delete");
  const isAdmin = has("email.settings") || user?.role === "ADMIN";

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me/permissions`, authHeaders());
        setPermissions(r.data.effective_permissions || []);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (permissions.length === 0) return;
    if (!canView) { setLoading(false); return; }
    fetchList();
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filter, consigneeFilter, permissions.length]);

  useEffect(() => {
    if (!canView) return;
    axios.get(`${API}/shipping-bills/consignee-options`, authHeaders())
      .then((r) => setConsigneeOpts(r.data?.consignees || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.length]);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/shipping-bills/config-status`, authHeaders());
      setCfgStatus(r.data);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    if (view !== "docs" || !canView) return;
    (async () => {
      setLoadingDocs(true);
      try {
        const params = new URLSearchParams();
        if (docTypeFilter !== "all") params.set("doc_type", docTypeFilter);
        const r = await axios.get(`${API}/shipping-bills/documents?${params.toString()}`, authHeaders());
        setDocuments(r.data.documents || []);
        setDocLabels(r.data.labels || {});
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load documents");
      } finally {
        setLoadingDocs(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, docTypeFilter, permissions.length]);

  const docPreviewUrl = (doc) =>
    `${API}/shipping-bills/documents/${doc.id}/preview?token=${encodeURIComponent(localStorage.getItem("token"))}`;

  const handleDocDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/shipping-bills/documents/${doc.id}/download`, authHeaders());
      if (res.data?.url) window.open(res.data.url, "_blank");
      else window.open(docPreviewUrl(doc), "_blank");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const handleReclassify = async (doc, newType) => {
    if (!newType || newType === doc.doc_type) return;
    try {
      const res = await axios.patch(`${API}/shipping-bills/documents/${doc.id}/reclassify`, { doc_type: newType }, authHeaders());
      const updated = res.data.document;
      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? updated : d)));
      toast.success(`Reclassified as ${updated.doc_type_label}`);
      if (newType === "SHIPPING_BILL") fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reclassify failed");
    }
  };

  const DOC_BADGE = {
    SHIPPING_BILL: "bg-sky-50 text-sky-700 border-sky-200",
    GATE_PASS: "bg-amber-50 text-amber-700 border-amber-200",
    CHECKLIST: "bg-violet-50 text-violet-700 border-violet-200",
    EGM: "bg-teal-50 text-teal-700 border-teal-200",
    ANNEXURE: "bg-zinc-50 text-zinc-600 border-zinc-200",
    OTHER: "bg-zinc-100 text-zinc-500 border-zinc-200",
  };

  const toggleBillDocs = async (row) => {
    if (expandedBill === row.id) { setExpandedBill(null); return; }
    setExpandedBill(row.id);
    if (billDocs[row.id]) return;
    try {
      const r = await axios.get(`${API}/shipping-bills/${row.id}/documents`, authHeaders());
      setBillDocs((prev) => ({ ...prev, [row.id]: r.data.documents || [] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load documents");
      setBillDocs((prev) => ({ ...prev, [row.id]: [] }));
    }
  };

  const fetchList = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filter !== "all") params.set("match_status", filter);
      if (consigneeFilter) params.set("consignee", consigneeFilter);
      const r = await axios.get(`${API}/shipping-bills?${params.toString()}`, authHeaders());
      setItems(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load shipping bills");
    } finally {
      setLoading(false);
    }
  };

  const openConfig = async () => {
    try {
      const r = await axios.get(`${API}/shipping-bills/config`, authHeaders());
      if (r.data?.configured) {
        setCfg({ ...EMPTY_CONFIG, ...r.data, password: "" });
      } else {
        setCfg(EMPTY_CONFIG);
      }
    } catch (_) {
      setCfg(EMPTY_CONFIG);
    }
    setTestResult(null);
    setCfgDialog(true);
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await axios.put(`${API}/shipping-bills/config`, cfg, authHeaders());
      toast.success("Shipping Bill mailbox saved");
      setCfgDialog(false);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save config");
    } finally {
      setSavingCfg(false);
    }
  };

  const testConfig = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/shipping-bills/config/test`, cfg, authHeaders());
      setTestResult(r.data);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || "Test failed" });
    } finally {
      setTesting(false);
    }
  };

  const handleFetch = async () => {
    setFetching(true);
    try {
      const r = await axios.post(`${API}/shipping-bills/fetch`, {}, authHeaders());
      const d = r.data || {};
      toast.success(`Fetched · ${d.new} new, ${d.skipped} already stored`, { duration: 6000 });
      await fetchList();
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fetch failed");
    } finally {
      setFetching(false);
    }
  };

  const handleManualUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF shipping bills are supported");
      if (uploadRef.current) uploadRef.current.value = "";
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/shipping-bills/upload`, fd, {
        headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Added Shipping Bill ${r.data?.sb_no || ""}`);
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const handleReparseAll = async () => {
    setReparsingAll(true);
    try {
      const r = await axios.post(`${API}/shipping-bills/reparse-all`, {}, authHeaders());
      const d = r.data || {};
      toast.success(`Re-parsed ${d.updated} bills · ${d.rate_found} now have an exchange rate`, { duration: 7000 });
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-parse all failed");
    } finally {
      setReparsingAll(false);
    }
  };

  const handleReparse = async (row) => {
    setRowBusy(row.id);
    try {
      await axios.post(`${API}/shipping-bills/${row.id}/reparse`, {}, authHeaders());
      toast.success("Re-parsed");
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-parse failed");
    } finally {
      setRowBusy(null);
    }
  };

  const handleDownload = async (row) => {
    try {
      const res = await axios.get(`${API}/shipping-bills/${row.id}/download`, authHeaders());
      const url = res.data?.url;
      if (!url) {
        window.open(`${API}/shipping-bills/${row.id}/download`, "_blank");
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.download = row.file_name || "shipping_bill.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.row) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/shipping-bills/${deleteDialog.row.id}`, authHeaders());
      toast.success("Deleted");
      setDeleteDialog({ open: false, row: null });
      await fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const previewUrl = (row) =>
    `${API}/shipping-bills/${row.id}/preview?token=${encodeURIComponent(localStorage.getItem("token"))}`;

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Shipping Bills" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Purchase Orders", to: "/safety/po", depth: 1 },
        { label: "Invoice cum Packing Lists", to: "/safety/packing-lists", depth: 1 },
        { label: "Shipping Bills", active: true, depth: 1 },
      ]}
    >
      <Toaster richColors position="top-right" />
      <div data-testid="shipping-bills-page">
        {/* Header */}
        <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Ship className="w-5 h-5 text-sky-600" />
            <div>
              <h1 className="text-xl font-bold tracking-tight">Shipping Bills</h1>
              <p className="text-xs text-zinc-500">
                ICEGATE shipping bills are fetched from the configured mailbox, parsed, stored on R2, and matched to packing
                lists by invoice number.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openConfig}
                  data-testid="sb-config-btn"
                >
                  <Settings2 className="w-4 h-4 mr-1.5" /> Mailbox Settings
                </Button>
                <input ref={uploadRef} type="file" accept=".pdf" className="hidden" onChange={handleManualUpload} data-testid="sb-upload-input" />
                <Button variant="outline" size="sm" onClick={() => uploadRef.current?.click()} data-testid="sb-upload-btn">
                  <FileUp className="w-4 h-4 mr-1.5" /> Upload PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReparseAll}
                  disabled={reparsingAll}
                  title="Re-parse all stored shipping bills to backfill fields like the USD→INR exchange rate"
                  data-testid="sb-reparse-all-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-1.5 ${reparsingAll ? "animate-spin" : ""}`} />
                  {reparsingAll ? "Re-parsing…" : "Re-parse All"}
                </Button>
                <Button
                  size="sm"
                  onClick={handleFetch}
                  disabled={fetching || !cfgStatus?.configured}
                  title={!cfgStatus?.configured ? "Configure the mailbox first" : "Fetch shipping bills from the mailbox now"}
                  data-testid="sb-fetch-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-1.5 ${fetching ? "animate-spin" : ""}`} />
                  {fetching ? "Fetching…" : "Fetch Now"}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Status banner */}
        {cfgStatus && (
          <div className="sf-surface px-4 py-2.5 mb-4 flex items-center gap-3 text-xs text-zinc-600 flex-wrap" data-testid="sb-status-banner">
            <Mail className="w-3.5 h-3.5 text-zinc-400" />
            {cfgStatus.configured ? (
              <>
                <span>Mailbox: <b className="font-medium text-zinc-800">{cfgStatus.email}</b></span>
                <span className="text-zinc-300">·</span>
                <span>Last fetch: {cfgStatus.last_fetch_at ? new Date(cfgStatus.last_fetch_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "never"}</span>
                {cfgStatus.last_fetch_status && (
                  <>
                    <span className="text-zinc-300">·</span>
                    <span>{cfgStatus.last_fetch_status}</span>
                  </>
                )}
              </>
            ) : (
              <span className="text-amber-700">No mailbox configured yet{isAdmin ? " — click “Mailbox Settings” to set it up." : "."}</span>
            )}
          </div>
        )}

        {/* View toggle: Bills vs all classified ICEGATE documents */}
        <div className="flex items-center gap-1 mb-4" data-testid="sb-view-toggle">
          <Button variant={view === "bills" ? "default" : "outline"} size="sm" className="h-8" onClick={() => setView("bills")} data-testid="sb-view-bills">
            <Ship className="w-4 h-4 mr-1.5" /> Shipping Bills
          </Button>
          <Button variant={view === "docs" ? "default" : "outline"} size="sm" className="h-8" onClick={() => setView("docs")} data-testid="sb-view-docs">
            <Files className="w-4 h-4 mr-1.5" /> All ICEGATE Documents
          </Button>
        </div>

        {/* Search + filter + list */}
        {view === "bills" && (
        <div className="sf-surface p-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search SB no, invoice, consignee, container, file…"
                className="pl-9"
                data-testid="sb-search"
              />
            </div>
            <div className="flex items-center gap-1">
              {["all", "matched", "unmatched"].map((f) => (
                <Button
                  key={f}
                  variant={filter === f ? "default" : "outline"}
                  size="sm"
                  className="h-8 capitalize"
                  onClick={() => setFilter(f)}
                  data-testid={`sb-filter-${f}`}
                >
                  {f}
                </Button>
              ))}
            </div>
            <select
              value={consigneeFilter}
              onChange={(e) => setConsigneeFilter(e.target.value)}
              className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs max-w-[220px]"
              data-testid="sb-consignee-filter"
            >
              <option value="">All consignees</option>
              {consigneeOpts.map((o) => (
                <option key={o.label} value={o.label}>{o.label} ({o.count})</option>
              ))}
            </select>
            <span className="text-xs text-zinc-500 ml-auto">{items.length} bill{items.length === 1 ? "" : "s"}</span>
          </div>

          <div className="overflow-x-auto rounded border bg-white">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600 mx-auto" />
              </div>
            ) : !canView ? (
              <div className="p-8 text-center text-zinc-500 text-sm">You do not have permission to view shipping bills.</div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center text-zinc-500">
                <Ship className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No shipping bills yet</p>
                {isAdmin && <p className="text-xs">Configure the mailbox and click “Fetch Now”, or upload a PDF.</p>}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">SB No</th>
                    <th className="px-3 py-2 text-left font-medium">SB Date</th>
                    <th className="px-3 py-2 text-left font-medium">Invoice No</th>
                    <th className="px-3 py-2 text-left font-medium">Consignee name</th>
                    <th className="px-3 py-2 text-left font-medium">Container</th>
                    <th className="px-3 py-2 text-left font-medium">Packing List</th>
                    <th className="px-3 py-2 text-left font-medium">Fetched</th>
                    <th className="px-3 py-2 text-right font-medium w-40">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <Fragment key={row.id}>
                    <tr className="border-t hover:bg-zinc-50" data-testid={`sb-row-${row.id}`}>
                      <td className="px-3 py-2 font-mono">{row.sb_no || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2">{row.sb_date || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2 font-mono">
                        {(row.invoice_nos || []).join(", ") || row.primary_invoice_no || <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2 truncate max-w-[180px]" title={row.consignee_name || ""}>
                        {row.consignee_name || <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{row.container_no || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2">
                        {row.matched_packing_list ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`sb-matched-${row.id}`}>
                            <Link2 className="w-3 h-3" /> {row.matched_packing_list.file_name?.slice(0, 22) || "Matched"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500 border border-zinc-200" data-testid={`sb-unmatched-${row.id}`}>
                            <Link2Off className="w-3 h-3" /> Unmatched
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-600">
                        <div>{row.fetched_at ? new Date(row.fetched_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</div>
                        <div className="text-zinc-400">{row.source === "email" ? "via email" : "manual"}</div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-1.5 text-xs text-zinc-600"
                            onClick={() => toggleBillDocs(row)}
                            title={row.has_gate_pass ? "Companion documents" : "Companion documents — no gate pass yet"}
                            data-testid={`sb-docs-toggle-${row.id}`}
                          >
                            {expandedBill === row.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            <Files className="w-3.5 h-3.5 ml-0.5" />
                            <span className="ml-0.5" data-testid={`sb-doc-count-${row.id}`}>{row.doc_count || 0}</span>
                            {!row.has_gate_pass && (
                              <span className="ml-1 inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`sb-no-gatepass-${row.id}`} title="No gate pass received yet">
                                no GP
                              </span>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => setPreviewDialog({ open: true, row })}
                            title="Preview"
                            data-testid={`sb-preview-${row.id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {canDownload && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleDownload(row)}
                              title="Download"
                              data-testid={`sb-download-${row.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleReparse(row)}
                              disabled={rowBusy === row.id}
                              title="Re-parse"
                              data-testid={`sb-reparse-${row.id}`}
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${rowBusy === row.id ? "animate-spin" : ""}`} />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                              onClick={() => setDeleteDialog({ open: true, row })}
                              title="Delete"
                              data-testid={`sb-delete-${row.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedBill === row.id && (
                      <tr key={`${row.id}-docs`} className="bg-zinc-50/60" data-testid={`sb-docs-expand-${row.id}`}>
                        <td colSpan={8} className="px-6 py-3">
                          {!billDocs[row.id] ? (
                            <div className="text-xs text-zinc-500">Loading documents…</div>
                          ) : billDocs[row.id].length === 0 ? (
                            <div className="text-xs text-zinc-500">No companion documents found for this shipping bill.</div>
                          ) : (
                            <div className="space-y-1.5">
                              <div className="text-xs font-medium text-zinc-500 mb-1">Documents received from ICEGATE for this shipment</div>
                              {billDocs[row.id].map((d) => (
                                <div key={d.id} className="flex items-center gap-2 text-sm" data-testid={`sb-companion-${d.id}`}>
                                  <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${DOC_BADGE[d.doc_type] || DOC_BADGE.OTHER}`}>
                                    {d.doc_type_label || d.doc_type}
                                  </span>
                                  <span className="truncate max-w-[280px] text-zinc-700" title={d.file_name}>{d.file_name}</span>
                                  <span className="text-xs text-zinc-400">{d.fetched_at ? new Date(d.fetched_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : ""}</span>
                                  <div className="ml-auto flex items-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => window.open(docPreviewUrl(d), "_blank")} title="Preview" data-testid={`sb-companion-preview-${d.id}`}>
                                      <Eye className="w-3.5 h-3.5" />
                                    </Button>
                                    {canDownload && (
                                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDocDownload(d)} title="Download" data-testid={`sb-companion-download-${d.id}`}>
                                        <Download className="w-3.5 h-3.5" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        )}

        {/* All ICEGATE documents view */}
        {view === "docs" && (
        <div className="sf-surface p-4" data-testid="sb-docs-card">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs text-zinc-500">Every file received from ICEGATE, stored on R2 and auto-classified. The actual shipping bills also appear in the Shipping Bills tab.</span>
            <span className="text-xs text-zinc-500 ml-auto">{documents.length} document{documents.length === 1 ? "" : "s"}</span>
          </div>
          <div className="flex items-center gap-1 mb-3 flex-wrap">
            {["all", "SHIPPING_BILL", "GATE_PASS", "CHECKLIST", "EGM", "ANNEXURE", "OTHER"].map((t) => (
              <Button key={t} variant={docTypeFilter === t ? "default" : "outline"} size="sm" className="h-8 text-xs"
                onClick={() => setDocTypeFilter(t)} data-testid={`sb-doc-filter-${t}`}>
                {t === "all" ? "All types" : (docLabels[t] || t)}
              </Button>
            ))}
          </div>
          <div className="overflow-x-auto rounded border bg-white">
            {loadingDocs ? (
              <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600 mx-auto" /></div>
            ) : documents.length === 0 ? (
              <div className="p-10 text-center text-zinc-500">
                <FileText className="w-10 h-10 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No documents yet</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Type</th>
                    <th className="px-3 py-2 text-left font-medium">File</th>
                    <th className="px-3 py-2 text-left font-medium">SB No</th>
                    <th className="px-3 py-2 text-left font-medium">Invoice</th>
                    <th className="px-3 py-2 text-left font-medium">Email</th>
                    <th className="px-3 py-2 text-left font-medium">Received</th>
                    <th className="px-3 py-2 text-right font-medium w-56">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((d) => (
                    <tr key={d.id} className="border-t hover:bg-zinc-50" data-testid={`sb-doc-row-${d.id}`}>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${DOC_BADGE[d.doc_type] || DOC_BADGE.OTHER}`} data-testid={`sb-doc-type-${d.id}`}>
                          {d.doc_type_label || d.doc_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 truncate max-w-[200px]" title={d.file_name}>{d.file_name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{d.sb_no || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2 font-mono text-xs">{(d.invoice_nos || []).join(", ") || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-2 text-xs text-zinc-600 truncate max-w-[180px]" title={d.email_subject || ""}>{d.email_subject || (d.source === "manual_upload" ? "manual upload" : "—")}</td>
                      <td className="px-3 py-2 text-xs text-zinc-600">{d.fetched_at ? new Date(d.fetched_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canDelete && (
                            <select
                              value={d.doc_type}
                              onChange={(e) => handleReclassify(d, e.target.value)}
                              className="h-7 text-xs border rounded px-1 bg-white text-zinc-600 mr-1"
                              title="Reclassify document"
                              data-testid={`sb-doc-reclassify-${d.id}`}
                            >
                              {["SHIPPING_BILL", "GATE_PASS", "CHECKLIST", "EGM", "ANNEXURE", "OTHER"].map((t) => (
                                <option key={t} value={t}>{docLabels[t] || t}</option>
                              ))}
                            </select>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => window.open(docPreviewUrl(d), "_blank")} title="Preview" data-testid={`sb-doc-preview-${d.id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {canDownload && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDocDownload(d)} title="Download" data-testid={`sb-doc-download-${d.id}`}>
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Config dialog */}
      <Dialog open={cfgDialog} onOpenChange={(o) => !o && !savingCfg && setCfgDialog(false)}>
        <DialogContent className="max-w-lg" data-testid="sb-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-sky-600" /> Shipping Bill Mailbox
            </DialogTitle>
            <DialogDescription>
              IMAP mailbox that receives ICEGATE shipping bills. Password is stored encrypted. Use an app-specific password for
              Gmail / Outlook.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-1">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-xs">IMAP Server</Label>
                <Input value={cfg.imap_server} onChange={(e) => setCfg({ ...cfg, imap_server: e.target.value })} placeholder="imap.gmail.com" data-testid="sb-cfg-server" />
              </div>
              <div>
                <Label className="text-xs">Port</Label>
                <Input type="number" value={cfg.imap_port} onChange={(e) => setCfg({ ...cfg, imap_port: e.target.value })} data-testid="sb-cfg-port" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email Address</Label>
              <Input value={cfg.email} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} placeholder="exports@company.com" data-testid="sb-cfg-email" />
            </div>
            <div>
              <Label className="text-xs">Password {cfgStatus?.configured && <span className="text-zinc-400">(leave blank to keep existing)</span>}</Label>
              <Input type="password" value={cfg.password} onChange={(e) => setCfg({ ...cfg, password: e.target.value })} placeholder="••••••••" data-testid="sb-cfg-password" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Sender Filter</Label>
                <Input value={cfg.sender_filter} onChange={(e) => setCfg({ ...cfg, sender_filter: e.target.value })} data-testid="sb-cfg-sender" />
              </div>
              <div>
                <Label className="text-xs">Folder</Label>
                <Input value={cfg.folder} onChange={(e) => setCfg({ ...cfg, folder: e.target.value })} data-testid="sb-cfg-folder" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded border p-2.5">
              <div className="flex items-center gap-2">
                <Switch checked={cfg.imap_use_ssl} onCheckedChange={(v) => setCfg({ ...cfg, imap_use_ssl: v })} data-testid="sb-cfg-ssl" />
                <span className="text-sm">Use SSL</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={cfg.auto_fetch_enabled} onCheckedChange={(v) => setCfg({ ...cfg, auto_fetch_enabled: v })} data-testid="sb-cfg-auto" />
                <span className="text-sm">Auto-fetch</span>
              </div>
            </div>
            {testResult && (
              <div className={`text-xs p-2 rounded border ${testResult.success ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`} data-testid="sb-test-result">
                {testResult.message}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={testConfig} disabled={testing} data-testid="sb-cfg-test">
              {testing ? "Testing…" : "Test Connection"}
            </Button>
            <Button onClick={saveConfig} disabled={savingCfg} data-testid="sb-cfg-save">
              {savingCfg ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={previewDialog.open} onOpenChange={(o) => !o && setPreviewDialog({ open: false, row: null })}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="sb-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ship className="w-5 h-5 text-sky-600" />
              Shipping Bill {previewDialog.row?.sb_no ? `· ${previewDialog.row.sb_no}` : ""}
            </DialogTitle>
            <DialogDescription>
              Invoice {(previewDialog.row?.invoice_nos || []).join(", ") || "—"} · {previewDialog.row?.exporter_name || ""}
            </DialogDescription>
          </DialogHeader>
          {previewDialog.row && (
            <iframe
              src={previewUrl(previewDialog.row)}
              title="Shipping Bill Preview"
              className="flex-1 w-full rounded border"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && !deleting && setDeleteDialog({ open: false, row: null })}>
        <DialogContent data-testid="sb-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> Delete shipping bill?
            </DialogTitle>
            <DialogDescription>
              {deleteDialog.row?.sb_no || deleteDialog.row?.file_name}<br />
              The stored PDF will be removed. This action is logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, row: null })} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="sb-delete-confirm">
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SafetyShell>
  );
}

export default ShippingBills;
